import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Button } from '~/components/ui';
import { activeRowCount } from '~/pages/buy-lists/lib/buyListFilters';
import type { useMyBuyListFilters } from '~/pages/buy-lists/lib/useMyBuyListFilters';
import { BuyListFilterControls } from '~/pages/buy-lists/sections/BuyListFilterControls';
import { BuyListTable } from '~/pages/buy-lists/sections/BuyListTable';
import type { BuyListTabProps } from '~/pages/buy-lists/sections/buyListTabProps';

type MyBuyListProps = BuyListTabProps & {
  readonly filters: ReturnType<typeof useMyBuyListFilters>;
  readonly onCreate: () => void;
};

export function MyBuyList({
  filters,
  locationOptions,
  vehicleOptions,
  productOptions,
  isLoadingProducts,
  onCreate,
  ...tableProps
}: MyBuyListProps) {
  const { t } = useTranslation('inventory');
  const emptyMessage =
    activeRowCount(filters.rows) > 0
      ? t('inventory.buy_list.filter.filtered_empty')
      : t('inventory.buy_list.my_lists_empty');

  return (
    <div className="mt-6 bg-neut-bg-alt rounded-md flex flex-col gap-2">
      <div
        className="flex items-center gap-3 flex-wrap justify-between p-2 px-4"
        data-testid="buy-list-mine-toolbar"
      >
        <Button
          variant="primary"
          size="sm"
          width="auto"
          onClick={onCreate}
          data-testid="buy-list-create"
        >
          <Plus size={14} aria-hidden="true" />
          {t('inventory.buy_list.create_action')}
        </Button>
        <BuyListFilterControls
          locations={locationOptions}
          transportVehicles={vehicleOptions}
          products={productOptions}
          isLoadingProducts={isLoadingProducts}
          views={filters.views}
        />
      </div>
      <BuyListTable
        {...tableProps}
        loadingMessage={t('inventory.buy_list.my_lists_loading')}
        emptyMessage={emptyMessage}
        loadFailedMessage={t('inventory.buy_list.my_lists_load_failed')}
        testId="buy-list-mine-table"
      />
    </div>
  );
}
